import { useEffect, useRef, useState } from "react";

interface VersionManifest {
  version: string;
}

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const response = await fetch("/version.json", { cache: "no-store" });
    if (!response.ok) return null;
    const manifest: VersionManifest = await response.json();
    return manifest.version;
  } catch {
    return null;
  }
}

/**
 * Polls the build-time version manifest on an interval and whenever the tab
 * regains visibility, and reports whether the server is now serving a
 * different version than the one the page loaded with. The manifest fetch
 * always bypasses the HTTP cache, since the whole point is detecting a
 * deploy that happened after the page loaded. Polling stops once a new
 * version is detected, since the flag only needs to flip once.
 *
 * @param intervalMs how often to poll while the tab is open, in milliseconds
 * @return true once a deployed version different from the one loaded at mount time is detected, false otherwise
 */
export default function useNewVersionAvailable(intervalMs: number): boolean {
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const loadedVersionRef = useRef<string | null>(null);
  const hasNewVersionRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const checkVersion = async () => {
      if (hasNewVersionRef.current) return;

      const deployedVersion = await fetchDeployedVersion();
      if (cancelled || deployedVersion === null) return;

      if (loadedVersionRef.current === null) {
        loadedVersionRef.current = deployedVersion;
        return;
      }

      if (deployedVersion !== loadedVersionRef.current) {
        hasNewVersionRef.current = true;
        setHasNewVersion(true);
      }
    };

    checkVersion();
    const interval = setInterval(checkVersion, intervalMs);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [intervalMs]);

  return hasNewVersion;
}
